import type { ThemeConfig, CSSVars } from './types';

export type ThemeOptions = {
    cssVarsPrefix: string;
};

const generateCssVars = (
    theme: ThemeConfig,
    options: ThemeOptions
): {
    cssDeclarations: string[];
    cssVars: CSSVars;
} => {
    const { cssVarsPrefix } = options;
    const _cssDeclarations: string[] = [];
    const cssVars = {} as CSSVars;

    function processTheme(obj: Record<string, any>, path: string[] = []): void {
        for (const [key, value] of Object.entries(obj)) {
            const currentPath = [...path, key];

            const isObject =
                typeof value === 'object' &&
                value !== null &&
                !Array.isArray(value);

            const hasValue = value !== null && value !== '';

            // Recursively process objects
            if (isObject) {
                processTheme(value, currentPath);
            } else if (hasValue) {
                const cssVar = `--${cssVarsPrefix}-${currentPath.join('-')}`;
                const cssValue = String(value);

                _cssDeclarations.push(`${cssVar}: ${cssValue};`);

                let current: Record<string, any> = cssVars;
                for (let i = 0; i < currentPath.length - 1; i++) {
                    const pathKey = currentPath[i];
                    if (!current[pathKey]) {
                        current[pathKey] = {};
                    }
                    current = current[pathKey] as Record<string, any>;
                }
                current[currentPath[currentPath.length - 1]] = `var(${cssVar})`;
            }
        }
    }

    processTheme(theme);

    return {
        cssDeclarations: _cssDeclarations,
        cssVars
    };
};

/**
 * Transforms ThemeConfig with actual values into CSS variables and typed references.
 *
 * @param theme - ThemeConfig with actual values (e.g., fonts: { body: 'Arial' })
 * @param options.cssVarsPrefix - CSS variable prefix (e.g., 'hb')
 * @returns cssDeclarations array and cssVars with CSS variable references
 *
 * @example
 * ```ts
 * const theme = createTheme(hbuiTheme, { cssVarsPrefix: 'hb' });
 * // theme.cssDeclarations → ['--hb-fonts-body: ProximaNova;', ...]
 * // theme.cssVars.fonts.body → 'var(--hb-fonts-body)'
 * ```
 */
export const createTheme = (theme: ThemeConfig, options: ThemeOptions) => {
    const { cssVarsPrefix } = options;
    const result = generateCssVars(theme, {
        cssVarsPrefix
    });

    return {
        cssDeclarations: result.cssDeclarations,
        cssVars: result.cssVars
    };
};
